import {
  BookOpen,
  ChartBar,
  ChartLineUp,
  TextAa,
  Compass,
  ListChecks,
  MathOperations,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import type { IconPadVariant } from '@/components/ui/icon-pad';

export interface MiniAppMeta {
  id: string;
  title: string;
  description: string;
  icon: PhosphorIcon;
  route: string;
  levels: string[];
  availableFor: Array<'tutor' | 'teacher' | 'admin' | 'eleve' | 'direction'>;
  /* Identite visuelle de la matiere/du domaine sur la vignette catalogue —
     benchmark (Kartable, Nomad Education, IXL) : une couleur dediee par
     matiere fait de la vignette une porte d'entree, pas une ligne de liste.
     Reutilise la palette IconPad existante (aucun token reserve par matiere
     a ce jour) ; defaut 'blue' si absent. */
  couleur?: IconPadVariant;
  /* Absent = maintenue. Renseigne = affiche un badge et rien d'autre : la carte
     reste ouvrable. Griser ou desactiver dirait « cassee » a l'utilisateur, ce
     que docs/mini-apps.md 3b.3 et 3b.4 interdisent. */
  nonMaintenue?: { depuis: string; raison: string };
  /* Un badge qui se retire. « nouveau » dit « regarde ici d'abord » ;
     « experimental » dit « ça marche, mais rien n'est conservé ». Aucun des deux
     ne désactive la carte : griser dirait « cassée ». */
  badge?: 'nouveau' | 'experimental';
  /* Bandeau libre en haut de carte : niveau scolaire (« Primaire ») ou public
     visé (« Enseignants ») quand la carte a besoin de se situer au premier
     coup d'oeil, avant même son titre. Texte libre (contrairement à `badge`,
     fermé) car les niveaux/destinataires possibles ne sont pas une liste
     fixe. Coexiste avec `badge`/`nonMaintenue` : un bandeau ne dit jamais si
     la carte est neuve, expérimentale ou abandonnée — ça reste le rôle des
     deux champs voisins. */
  bandeau?: string;
}

export const MINI_APPS: MiniAppMeta[] = [
  {
    id: 'fluence-enseignant',
    title: 'Français',
    description: "Assigner une lecture, la faire passer, suivre la progression. Les mesures sont enregistrées et se retrouvent d'une séance à l'autre.",
    icon: ChartLineUp,
    route: '/mini-apps/fluence-enseignant',
    levels: ['CP', 'CE1', 'CE2', 'CM1', 'CM2'],
    availableFor: ['teacher', 'admin'],
    bandeau: 'Primaire',
    couleur: 'blue',
  },
  {
    id: 'francais-primaire',
    title: 'Fluence & Compréhension — prototype',
    description: "La maquette complète, pour se faire une idée. Elle tourne sans base : rien n'est conservé d'une visite à l'autre.",
    icon: TextAa,
    route: '/mini-apps/francais-primaire',
    levels: ['CP', 'CE1', 'CE2', 'CM1', 'CM2'],
    availableFor: ['tutor', 'teacher', 'admin'],
    nonMaintenue: {
      depuis: '2026-09-12',
      raison: "Remplacée par « Français », la vraie mini-app Fluence & Compréhension. Cette maquette n'évolue plus et sera retirée.",
    },
    couleur: 'blue',
  },
  {
    id: 'fluence-eleve',
    title: 'Fluence — m\'entraîner',
    description: "Choisis un texte, lis-le à voix haute, réponds aux questions. C'est pour s'exercer : rien n'est enregistré. La vraie lecture se fait en classe.",
    icon: BookOpen,
    route: '/mini-apps/fluence-eleve',
    levels: ['CP', 'CE1', 'CE2', 'CM1', 'CM2'],
    /* `eleve` D'ABORD, parce que c'est son écran. Il manquait : la carte faite
       pour l'enfant était invisible à l'enfant, et seuls les adultes la
       voyaient. Chaîne vérifiée selon docs/mini-apps.md 3.8 — route ouverte à
       tout compte, procédures `protectedProcedure`, et la correction
       d'entraînement n'exige aucun droit puisqu'elle n'écrit rien. */
    availableFor: ['eleve', 'tutor', 'teacher', 'admin'],
    bandeau: 'Primaire',
    couleur: 'blue',
  },
  {
    id: 'fluence-direction',
    title: 'Fluence — vue de l\'école',
    description: "Où en est la lecture niveau par niveau, et surtout où la mesure manque : un niveau qu'on ne mesure pas est plus difficile à aider qu'un niveau en difficulté.",
    icon: ChartLineUp,
    route: '/mini-apps/fluence-direction',
    levels: ['CP', 'CE1', 'CE2', 'CM1', 'CM2'],
    /* `direction` n'existait pas dans cette union : un directeur ouvrait le
       catalogue et n'y voyait RIEN. C'est la seule carte qui s'ouvre a lui —
       les autres relevent de la classe, pas du pilotage d'ecole. */
    availableFor: ['direction', 'admin'],
    bandeau: 'Primaire',
    couleur: 'blue',
  },
  {
    id: 'cap',
    title: 'Synchronisation de la programmation et du calendrier',
    description: 'Prototype du flux de planification annuelle : séquences, budget-temps, placement et suivi.',
    icon: Compass,
    route: '/mini-apps/cap',
    levels: ['Maternelle', 'Primaire', 'Collège', 'Lycée'],
    availableFor: ['teacher', 'admin'],
    bandeau: 'Enseignants',
    couleur: 'orange',
  },
  {
    id: 'hg-college-qcm',
    /* Appellation officielle de la discipline, demandée par Ismaël le 12/09 :
       l'EMC n'est pas un complément de l'histoire-géographie, c'est un
       enseignement nommé à part dans les programmes et évalué par une
       sous-épreuve distincte du DNB. Le titre le dit. */
    title: 'Histoire-géographie et Enseignement moral et civique (EMC)',
    description:
      "Questions du programme officiel de la 6e à la 3e, en histoire, en géographie et en EMC, avec positionnement critérié à quatre niveaux. Contenu servi par la base et sourcé sur les ressources ministérielles.",
    icon: ListChecks,
    route: '/mini-apps/hg-college-qcm',
    levels: ['6e', '5e', '4e', '3e'],
    availableFor: ['eleve', 'teacher', 'admin'],
    badge: 'nouveau',
    bandeau: 'Collège',
    couleur: 'purple',
  },
  {
    id: 'hg-college-enseignant',
    title: 'Histoire-géo — diagnostic de classe',
    description:
      "Les notions et confusions les plus fréquentes de la classe, tirées du diagnostic par item du QCM — jamais un classement d'élèves.",
    icon: ChartBar,
    route: '/mini-apps/hg-college-enseignant',
    levels: ['6e', '5e', '4e', '3e'],
    availableFor: ['teacher', 'admin'],
    badge: 'nouveau',
    bandeau: 'Collège',
  },
  {
    id: 'francais-college',
    title: 'Français collège — prototype',
    description: "Prototype en construction : les contenus sont en brouillon et rien n'est conservé.",
    icon: BookOpen,
    route: '/mini-apps/francais-college',
    levels: ['6e'],
    availableFor: ['teacher', 'admin'],
    badge: 'experimental',
    bandeau: 'Collège',
    couleur: 'blue',
  },
  {
    id: 'maths-primaire',
    title: 'Mathématiques',
    description: "Prototype couvrant 473 des 477 objectifs du programme, du CP au CM2 : 237 fiches, 4 358 exercices, écran de couverture BO exportable. Élèves et classes sont des données de démonstration, stockées localement.",
    icon: MathOperations,
    route: '/mini-apps/maths-primaire',
    levels: ['CP', 'CE1', 'CE2', 'CM1', 'CM2'],
    availableFor: ['teacher', 'admin'],
    bandeau: 'Primaire',
    couleur: 'green',
  },
];
